import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const COOKIE_MD = `# Cookie Policy — Pravadh GovernX

**Last updated: June 2026**

This Cookie Policy explains how Pravadh Labs ("we," "us," "our") uses cookies and similar technologies on Pravadh GovernX ("GovernX," "the Service").

## 1. What Are Cookies

Cookies are small text files placed on your device when you visit a website. They help the website function, remember your preferences, and understand how the site is used.

## 2. Types of Cookies We Use

**Essential cookies (always active)**
Required for the Service to function — session management, authentication, security, and remembering your login state. The Service cannot operate without these. They cannot be disabled.

**Analytics cookies (optional)**
Help us understand how users interact with GovernX — which pages are visited, how long, and general usage patterns — so we can improve the product. These do not identify you personally beyond your account activity within the Service.

**Marketing cookies (optional)**
Used to measure the effectiveness of our marketing efforts, including cross-product promotion (e.g., tracking referrals between Pravadh GovernX and Pravadh IQ) and any advertising campaigns we may run.

## 3. Third-Party Cookies

We may use third-party services (such as analytics or payment processing providers) that set their own cookies. These are governed by the respective third party's privacy and cookie policies. Examples may include:
- Payment processing (Razorpay)
- Analytics tools
- Customer support/chat tools

## 4. Managing Your Cookie Preferences

You can manage your cookie preferences at any time via the "Manage preferences" option in the cookie banner, or through your account settings. You can also control cookies through your browser settings, though disabling essential cookies may prevent the Service from functioning correctly.

## 5. Changes to This Policy

We may update this Cookie Policy from time to time. Material changes will be reflected with an updated "Last updated" date and, where appropriate, a renewed consent prompt.

## 6. Contact

For questions about this Cookie Policy, contact us at privacy@pravadhlabs.com.`;

function renderInline(text, keyBase) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${keyBase}-${i}`} className="font-semibold text-foreground">
        {p.slice(2, -2)}
      </strong>
    ) : (
      <React.Fragment key={`${keyBase}-${i}`}>{p}</React.Fragment>
    )
  );
}

function Markdown({ source }) {
  const lines = source.split("\n");
  const blocks = [];
  let list = null;
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith("- ")) {
      if (!list) list = [];
      list.push(line.slice(2));
      return;
    }
    if (list) {
      blocks.push(
        <ul key={`ul-${idx}`} className="my-3 list-disc space-y-1.5 pl-6 text-sm text-muted-foreground">
          {list.map((li, i) => (
            <li key={i}>{renderInline(li, `li-${idx}-${i}`)}</li>
          ))}
        </ul>
      );
      list = null;
    }
    if (line === "") return;
    if (line.startsWith("# ")) {
      blocks.push(
        <h1 key={idx} className="font-display text-3xl font-bold tracking-tight text-foreground lg:text-4xl">
          {line.slice(2)}
        </h1>
      );
    } else if (line.startsWith("## ")) {
      blocks.push(
        <h2 key={idx} className="mt-8 font-display text-lg font-bold tracking-tight text-foreground">
          {line.slice(3)}
        </h2>
      );
    } else {
      blocks.push(
        <p key={idx} className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {renderInline(line, `p-${idx}`)}
        </p>
      );
    }
  });
  if (list) {
    blocks.push(
      <ul key="ul-end" className="my-3 list-disc space-y-1.5 pl-6 text-sm text-muted-foreground">
        {list.map((li, i) => (
          <li key={i}>{renderInline(li, `li-end-${i}`)}</li>
        ))}
      </ul>
    );
  }
  return <>{blocks}</>;
}

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-background" data-testid="cookie-policy-page">
      <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 lg:py-16">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          data-testid="cookie-policy-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <article>
          <Markdown source={COOKIE_MD} />
        </article>
      </div>
    </div>
  );
}
